import { HtmlContent, makeStyles, Typography, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { RenderableItem } from '@paintscout/util/builder'
import { getQuoteOptions } from '@paintscout/util/builder'
import classNames from 'classnames'
import React from 'react'
import striptags from 'striptags'
import { isHtmlEmpty } from '../presentation/util/isHtmlEmpty'
import { useQuote } from '../context'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { splitTrailingMeasurement } from '../Quote/views/WorkOrderView/WorkOrderCard/splitTrailingMeasurement'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'

export interface WorkOrderItemRowsProps {
  item: RenderableItem
  /** Show the per-row hour values (respects the section's "Show all hours" setting). */
  showHours?: boolean
  /**
   * `card` (default) is the standalone hour card: padded rows, an inset rule between them, and bold
   * sub-item names. `embedded` drops all three — the Options table row supplies its own padding, the
   * rows stack on a gap with no rule, and sub-item names read as plain lines (ENG-1850).
   */
  variant?: 'card' | 'embedded'
  /** Extra class per row, so a caller can add its own hover tint / borders. */
  rowClassName?: string
  /** Extra class per divider, for the same reason. */
  dividerClassName?: string
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const rule = `1px solid ${neutral.P90}`
    return {
      row: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.25),
        padding: theme.spacing(2),
        transition: 'background-color 150ms ease'
      },
      // Embedded in a table row that already pads itself; consecutive rows stack on a gap, since this
      // variant draws no rule between them.
      rowEmbedded: {
        alignItems: 'flex-start',
        // Right padding only: it sets the hour values off the row's edge, in line with the text inside
        // the Total bar below them.
        padding: theme.spacing(0, 1.5, 0, 0),
        '& + &': {
          marginTop: theme.spacing(1.5)
        }
      },
      // Between-row rule, inset from the card edges with a little air above and below.
      rowDivider: {
        padding: theme.spacing(0.5, 2),
        transition: 'background-color 150ms ease'
      },
      rowDividerLine: {
        borderTop: rule
      },
      rowMain: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5)
      },
      subName: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40,
        lineHeight: 1.4
      },
      subLine: {
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40,
        lineHeight: 1.4
      },
      rowValue: {
        flexShrink: 0,
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(14),
        color: neutral.P40
      }
    }
  },
  { name: 'WorkOrderItemRows' }
)

/** Resolve an item/sub-item name that may be a plain string or an overridable {useCustom,custom,default}. */
export function resolveName(name: RenderableItem['name']): string {
  if (!name) {
    return ''
  }
  if (typeof name === 'string') {
    return name
  }
  return (name.useCustom ? name.custom : name.default) ?? ''
}

function formatHours(value: string | number | undefined): string {
  const n = typeof value === 'string' ? parseFloat(value) : value
  return Number.isFinite(n as number) ? String(n) : ''
}

/** Coerce an hour field (string | number | undefined) to a number; anything unusable becomes 0. */
export function toHours(value: unknown): number {
  const n = typeof value === 'string' ? parseFloat(value) : (value as number)
  return Number.isFinite(n) ? n : 0
}

/** Trim a trailing .0 so whole hours read "4", not "4.0" (matches the mock). */
export function formatHourTotal(value: number): string {
  return value % 1 === 0 ? String(value) : value.toFixed(1)
}

/**
 * An item's hour split for the Total bar: prefer the item's own prep/painting fields (they survive
 * overrides), falling back to summing its sub-items. "total" stays the item value so the footer
 * always agrees with the header/quote totals even when a prep+painting sum wouldn't.
 */
export function getItemHourTotals(item: RenderableItem): { prep: number; painting: number; total: number } {
  const subItems = item.subItems ?? []
  const sumSubItems = (field: 'prep' | 'painting') =>
    subItems.reduce((total, subItem) => total + toHours(subItem.additionalFields?.[field]), 0)
  return {
    prep: item.additionalFields?.prep != null ? toHours(item.additionalFields.prep) : sumSubItems('prep'),
    painting:
      item.additionalFields?.painting != null ? toHours(item.additionalFields.painting) : sumSubItems('painting'),
    total: toHours(item.value)
  }
}

/**
 * The crew description is "{details}\n{product}\n{product}…". The crew item builders hand us those
 * pieces on `additionalFields.crewDescriptionParts`; splitting the joined string is the fallback for
 * anything that predates them (or a non-crew consumer).
 */
function splitCrewDescription(
  description: string,
  parts?: { details?: string; products?: string[] }
): { details: string; products: string[] } {
  if (parts) {
    return { details: parts.details ?? '', products: parts.products ?? [] }
  }
  const [details = '', ...products] = description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  return { details, products }
}

/**
 * One row per sub-item of a Work Order item — name, crew description (prep/labour then one line per
 * product), report text, crew note and Coats, with the row's hours on the right. Shared by the hour
 * cards (`WorkOrderCardNext`, which wraps these in its bordered card) and the Work Order Options
 * table (`OptionsTableNext`, which embeds them in a table row without dividers) (ENG-1850).
 */
export default function WorkOrderItemRows({
  item,
  showHours,
  variant = 'card',
  rowClassName,
  dividerClassName
}: WorkOrderItemRowsProps) {
  const isEmbedded = variant === 'embedded'
  const classes = useStyles()
  const { __ } = useTranslation()
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const { showWorkOrderViewDescriptions, showWorkOrderProducts } = getQuoteOptions({ quote, options })
  const showCoats = options.options.quotes?.showWorkOrderCoats

  const subItems = item.subItems ?? []
  const hasSubItems = subItems.length > 0

  const rows = hasSubItems
    ? subItems.map((subItem) => ({
        key: subItem.key ?? subItem.name,
        name: subItem.name,
        ...splitCrewDescription(subItem.description ?? '', subItem.additionalFields?.crewDescriptionParts),
        // Crew notes always show; the area/substrate report text is gated by the account's
        // "work order descriptions" setting (same split the legacy WorkOrderCard used).
        crewNote: subItem.crewNote ?? '',
        reportText: subItem.reportText ?? '',
        coats: subItem.additionalFields?.coats ? String(subItem.additionalFields.coats) : '',
        value: subItem.value
      }))
    : [
        {
          key: item.key,
          name: '',
          details: resolveName(item.name)
            ? (item.description?.useCustom ? item.description?.custom : item.description?.default) ?? ''
            : '',
          products: [] as string[],
          crewNote: '',
          reportText: '',
          coats: '',
          value: item.value
        }
      ]

  // Translate the row content the same way the legacy WorkOrderCard did: sub-item names and
  // descriptions go through the live note-translation batch (customer view) / pre-baked map, and then
  // through translateStructuredWorkOrderContent for the structured "coats"/measurement labels. The
  // trailing measurement on a description is split off so only the prose is sent for translation.
  // All inputs are gathered into one stable array (hooks can't run per-row) and mapped back in order.
  // Products go through as one newline-joined block (split back apart after translating) so each row
  // contributes a fixed number of inputs regardless of how many products it has.
  const rowProductParts = rows.map((row) => splitTrailingMeasurement(row.products.join('\n')))
  const translationInputs = rows.flatMap((row, i) => [
    row.name || '',
    row.details,
    rowProductParts[i].base,
    row.crewNote,
    row.reportText
  ])
  const [translatedInputs] = useTranslatedNoteContent(translationInputs)
  let ti = 0
  const translatedRows = rows.map((row, i) => {
    const translatedRowName = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.name || '', __)
    const translatedDetails = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.details, __)
    const translatedProductBase = translatedInputs[ti++] || rowProductParts[i].base
    const translatedProducts = translateStructuredWorkOrderContent(
      translatedProductBase + rowProductParts[i].measurement,
      __
    )
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
    const translatedCrewNote = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.crewNote, __)
    const translatedReportText = translateStructuredWorkOrderContent(translatedInputs[ti++] || row.reportText, __)
    return {
      ...row,
      name: translatedRowName,
      details: translatedDetails,
      products: translatedProducts,
      crewNote: translatedCrewNote,
      reportText: translatedReportText
    }
  })
  return (
    <>
      {translatedRows.map((row, rowIndex) => (
        <React.Fragment key={row.key}>
          {/* Inset rule between rows (the first row's top border comes from the card header instead). */}
          {!isEmbedded && rowIndex > 0 && (
            <div className={classNames(classes.rowDivider, dividerClassName)}>
              <div className={classes.rowDividerLine} />
            </div>
          )}
          <div className={classNames(classes.row, { [classes.rowEmbedded]: isEmbedded }, rowClassName)}>
            <div className={classes.rowMain}>
              {row.name && (
                <Typography className={isEmbedded ? classes.subLine : classes.subName}>{row.name}</Typography>
              )}
              {/* The crew description (prep/labour, then one line per product) always shows — matching
                  the legacy card. Only the area/substrate report text sits behind the account setting;
                  the product lines follow the nav bar's "Hide all products". Rendered as HTML because a
                  line item's description is rich text (the crew-built strings are plain, which
                  HtmlContent passes through and pre-wraps itself). */}
              {!isHtmlEmpty(row.details) && (
                <Typography component="div" className={classes.subLine}>
                  <HtmlContent content={row.details} />
                </Typography>
              )}
              {/* Index keys: two different products can format to the same string (same label, colour
                  and volume), which would collide on a string key. */}
              {showWorkOrderProducts !== false &&
                row.products.map((product, productIndex) => (
                  <Typography className={classes.subLine} key={productIndex}>
                    {product}
                  </Typography>
                ))}
              {showWorkOrderViewDescriptions && !isHtmlEmpty(row.reportText) && (
                <Typography component="div" className={classes.subLine}>
                  <HtmlContent content={row.reportText} />
                </Typography>
              )}
              {!!striptags(row.crewNote).trim() && (
                <Typography component="div" className={classes.subLine}>
                  <HtmlContent classes={{ root: 'crew-note-highlight' }} content={row.crewNote} />
                </Typography>
              )}
              {showCoats && row.coats && (
                <Typography className={classes.subLine}>{`${__('Coats')}: ${row.coats}`}</Typography>
              )}
            </div>
            {/* The embedded rows carry their own unit — unlike a card, there's no header band with
                the "hr" label above them. */}
            {showHours && (
              <span className={classes.rowValue}>
                {formatHours(row.value)}
                {isEmbedded ? ` ${__('hrs')}` : ''}
              </span>
            )}
          </div>
        </React.Fragment>
      ))}
    </>
  )
}
